import React, { useEffect, useRef, useState } from "react";
import { styled, StyledFieldLabelLight } from "@iventis/styles";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { convertLengthFeetToMeters, convertLengthMeasurement } from "@iventis/utilities";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { TextFieldSolid } from "@iventis/styles/src/components/text-field-solid";
import { preventMouseWheelInput } from "@iventis/utilities/src/input/number-input-helpers";

type UnitSuffix = "m" | "km" | "ft" | "mi";

// eslint-disable-next-line prettier/prettier
type InputValue = `${string} ${UnitSuffix}` | "";

/**
 * Converts the given value into a string with an appropriate unit siffix, depending on the given unit of measurement and the value size
 * If the value is null, returns an empty string
 * */
const toValueWithUnit = (value: number, unitOfMeasurement: UnitOfMeasurement) => (value ? convertLengthMeasurement(value, unitOfMeasurement) : "");

/** Maps a unit suffix to it's value getter (from metres) */
const unitToMetreConversion = {
    m: (v) => v,
    km: (v) => v * 1000,
    ft: convertLengthFeetToMeters,
    mi: (v) => convertLengthFeetToMeters((v ?? 0) * 5280),
};

/** Input field for handling the real world length of something */
export const LengthField: React.FunctionComponent<{
    /** Length in metres */
    value: number;
    /**
     * @param value Length in metres
     */
    valueChanged: (value: number) => void;
    unitOfMeasurement: UnitOfMeasurement;
    disabled?: boolean;
    fieldTestId?: string;
    inputTestId?: string;
    className?: string;
}> = ({ value, valueChanged, disabled = false, unitOfMeasurement, inputTestId, fieldTestId, className }) => {
    const translate = useIventisTranslate();

    // Local state
    const [valueWithUnit, setValueWithUnit] = useState<InputValue>(toValueWithUnit(value, unitOfMeasurement));
    const inputRef = useRef<HTMLInputElement>(null);

    // Derived state
    const unit: UnitSuffix = (valueWithUnit?.split(" ")[1] ?? convertLengthMeasurement(value, unitOfMeasurement)?.split(" ")[1]) as UnitSuffix;
    const inputValue = valueWithUnit?.split(" ")[0] ?? "";
    const inputValueInMetres = unitToMetreConversion[unit]?.(Number(inputValue));

    // State synching
    useEffect(() => {
        // If we're focused on the input, don't update it
        if (document.activeElement === inputRef.current) {
            return;
        }
        setValueWithUnit((current) => {
            // Only update the input if there's been a significant change
            // For example, if the value goes from 1078.46 to 1082.91, both will result in 1.08 km, so there's no point updating the input value
            const existingValue = convertLengthMeasurement(inputValueInMetres, unitOfMeasurement);
            const incomingValue = convertLengthMeasurement(value, unitOfMeasurement);
            if (existingValue === incomingValue) {
                return current;
            }
            const newInputValue = toValueWithUnit(value, unitOfMeasurement);
            return newInputValue;
        });
    }, [value, unitOfMeasurement]);

    // Input handler
    const handleValueChanged = (newInputValue: string) => {
        const newInputValueNumber = Number(newInputValue);
        if (Number.isNaN(newInputValueNumber)) {
            return;
        }
        // eslint-disable-next-line prettier/prettier
        setValueWithUnit(`${newInputValue} ${unit}`);
        if (newInputValueNumber === Number(inputValue) || newInputValueNumber === 0) {
            return;
        }
        valueChanged(unitToMetreConversion[unit](newInputValueNumber));
    };

    return (
        <StyledContainer className={className} data-testid={fieldTestId}>
            <StyledFieldLabelLight>
                <span>{translate(Content.map10.systemDataFieldNames.MapObjectLength)}</span> ({unit})
            </StyledFieldLabelLight>
            <TextFieldSolid
                inputRef={inputRef}
                type="number"
                value={inputValue}
                onChange={(event) => handleValueChanged(event.target.value)}
                disabled={disabled}
                data-testid={inputTestId}
                fullWidth
                onBlur={() => setValueWithUnit(toValueWithUnit(value, unitOfMeasurement))}
                onWheel={preventMouseWheelInput}
            />
        </StyledContainer>
    );
};

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;
